import type { Character, Ranking } from '@/schemas';
import { CHARACTERS } from '@/schemas';
import { saveScore, getTopRankings, isTopScore } from '@/firebase/firestore';

export type Screen = 'start' | 'characterSelect' | 'game' | 'gameOver' | 'ranking';

/**
 * HTMLエスケープ関数（XSS対策）
 */
function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

export class UIManager {
  private currentScreen: Screen = 'start';
  private onStartGame?: (character: Character) => void;
  private onRetry?: () => void;
  private onBackToStart?: () => void;
  private currentScore: number = 0;
  private lastSubmittedScore: number = 0;
  private lastSubmittedName: string = '';

  constructor() {
    this.setupEventListeners();
    this.displayVersion();
  }

  /**
   * バージョンを表示
   */
  private displayVersion(): void {
    const versionElement = document.getElementById('app-version');
    if (versionElement) {
      versionElement.textContent = `v${__APP_VERSION__}`;
    }
  }

  /**
   * イベントリスナーを設定
   */
  private setupEventListeners(): void {
    // スタート画面
    const startButton = document.getElementById('start-button');
    if (startButton) {
      startButton.addEventListener('click', () => this.showCharacterSelectScreen());
    }

    const rankingButton = document.getElementById('ranking-button');
    if (rankingButton) {
      rankingButton.addEventListener('click', () => this.showRankingScreen());
    }

    // キャラクター選択画面
    const yukariButton = document.getElementById('select-yukari');
    if (yukariButton) {
      yukariButton.addEventListener('click', () => this.selectCharacter('yukari'));
    }

    const yuzukoButton = document.getElementById('select-yuzuko');
    if (yuzukoButton) {
      yuzukoButton.addEventListener('click', () => this.selectCharacter('yuzuko'));
    }

    const characterBackButton = document.getElementById('character-back-button');
    if (characterBackButton) {
      characterBackButton.addEventListener('click', () => this.showStartScreen());
    }

    // ゲームオーバー画面
    const retryButton = document.getElementById('retry-button');
    if (retryButton) {
      retryButton.addEventListener('click', () => {
        if (this.onRetry) this.onRetry();
      });
    }

    const backToStartButton = document.getElementById('back-to-start-button');
    if (backToStartButton) {
      backToStartButton.addEventListener('click', () => {
        if (this.onBackToStart) this.onBackToStart();
      });
    }

    const submitScoreButton = document.getElementById('submit-score-button');
    if (submitScoreButton) {
      submitScoreButton.addEventListener('click', () => this.handleSubmitScore());
    }

    // ランキング画面
    const rankingBackButton = document.getElementById('ranking-back-button');
    if (rankingBackButton) {
      rankingBackButton.addEventListener('click', () => this.showStartScreen());
    }

    const shareXButton = document.getElementById('share-x-button');
    if (shareXButton) {
      shareXButton.addEventListener('click', () => this.shareToX());
    }
  }

  /**
   * 画面を表示
   */
  private showScreen(screenId: string): void {
    const screens = [
      'start-screen',
      'character-select-screen',
      'game-screen',
      'game-over-screen',
      'ranking-screen',
    ];

    screens.forEach((id) => {
      const screen = document.getElementById(id);
      if (screen) {
        if (id === screenId) {
          screen.classList.remove('hidden');
        } else {
          screen.classList.add('hidden');
        }
      }
    });
  }

  /**
   * スタート画面を表示
   */
  public showStartScreen(): void {
    this.currentScreen = 'start';
    this.showScreen('start-screen');
  }

  /**
   * キャラクター選択画面を表示
   */
  public showCharacterSelectScreen(): void {
    this.currentScreen = 'characterSelect';
    this.showScreen('character-select-screen');
  }

  /**
   * ゲーム画面を表示
   */
  public showGameScreen(): void {
    this.currentScreen = 'game';
    this.showScreen('game-screen');

    // スコアをリセット
    this.updateScore(0);
  }

  /**
   * ゲームオーバー画面を表示
   */
  public async showGameOverScreen(score: number): Promise<void> {
    this.currentScreen = 'gameOver';
    this.currentScore = score;

    // 最終スコアを表示
    const finalScoreElement = document.getElementById('final-score');
    if (finalScoreElement) {
      finalScoreElement.textContent = score.toString();
    }

    // トップ10判定
    const nameInputContainer = document.getElementById('name-input-container');
    if (nameInputContainer) {
      const isTop10 = await isTopScore(score);
      if (isTop10) {
        nameInputContainer.classList.remove('hidden');
        // 入力欄をクリア
        const nameInput = document.getElementById('player-name-input') as HTMLInputElement;
        if (nameInput) {
          nameInput.value = '';
        }
        // ボタンの状態をリセット
        const submitButton = document.getElementById('submit-score-button') as HTMLButtonElement;
        if (submitButton) {
          submitButton.disabled = false;
          submitButton.textContent = '登録';
        }
      } else {
        nameInputContainer.classList.add('hidden');
      }
    }

    this.showScreen('game-over-screen');
  }

  /**
   * ランキング画面を表示
   */
  public async showRankingScreen(showShareButton: boolean = false): Promise<void> {
    this.currentScreen = 'ranking';

    // ランキングリストを表示
    const rankingList = document.getElementById('ranking-list');
    if (rankingList) {
      rankingList.innerHTML = '<p>読み込み中...</p>';

      const rankings = await getTopRankings();

      if (rankings.length === 0) {
        rankingList.innerHTML = '<p>まだランキングデータがありません</p>';
      } else {
        let html = '<ol style="text-align: left; font-size: clamp(14px, 3.5vw, 18px);">';
        rankings.forEach((ranking: Ranking) => {
          const date = new Date(ranking.timestamp).toLocaleDateString('ja-JP');
          // XSS対策: プレイヤー名とスコアをエスケープ
          const escapedPlayerName = escapeHtml(ranking.playerName);
          const escapedScore = escapeHtml(ranking.score.toString());
          const escapedDate = escapeHtml(date);
          html += `<li style="margin: 12px 0; padding: 8px; background: rgba(0,0,0,0.05); border-radius: 4px;">
            <strong>${escapedPlayerName}</strong> - ${escapedScore}点 <small>(${escapedDate})</small>
          </li>`;
        });
        html += '</ol>';
        rankingList.innerHTML = html;
      }
    }

    // X共有ボタンの表示/非表示
    const shareButton = document.getElementById('share-x-button');
    if (shareButton) {
      if (showShareButton) {
        shareButton.classList.remove('hidden');
      } else {
        shareButton.classList.add('hidden');
      }
    }

    this.showScreen('ranking-screen');
  }

  /**
   * キャラクターを選択
   */
  private selectCharacter(characterId: 'yukari' | 'yuzuko'): void {
    const character = CHARACTERS.find((c: Character) => c.id === characterId);

    if (character && this.onStartGame) {
      this.onStartGame(character);
    }
  }

  /**
   * スコア登録を処理
   */
  private async handleSubmitScore(): Promise<void> {
    const nameInput = document.getElementById('player-name-input') as HTMLInputElement;
    if (!nameInput) return;

    const playerName = nameInput.value.trim();
    if (!playerName) {
      alert('名前を入力してください');
      return;
    }

    if (playerName.length > 20) {
      alert('名前は20文字以内で入力してください');
      return;
    }

    // 保存中は再送信を防ぐ
    const submitButton = document.getElementById('submit-score-button') as HTMLButtonElement;
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.textContent = '登録中...';
    }

    const success = await saveScore(this.currentScore, playerName);

    if (success) {
      // 登録成功時にスコアと名前を保存
      this.lastSubmittedScore = this.currentScore;
      this.lastSubmittedName = playerName;

      // 入力欄を非表示
      const nameInputContainer = document.getElementById('name-input-container');
      if (nameInputContainer) {
        nameInputContainer.classList.add('hidden');
      }
      // ランキング画面を表示（X共有ボタンを表示）
      await this.showRankingScreen(true);
    } else {
      alert('スコアの登録に失敗しました。\nFirebaseの設定を確認してください。');
      // ボタンを元に戻す
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.textContent = '登録';
      }
    }
  }

  /**
   * スコアを更新
   */
  public updateScore(score: number): void {
    const scoreDisplay = document.getElementById('score-display');
    if (scoreDisplay) {
      scoreDisplay.textContent = score.toString();
    }
  }

  /**
   * 難易度上昇メッセージを表示（3.5秒後に自動的に消える）
   */
  public showDifficultyMessage(): void {
    const messages = [
      'はい来た、難易度上昇ターン',
      'なんか知らんけど難易度上がってない？',
      'あっ、これあれだ…急に難易度あげるやつだ…',
      'あ〜〜これはもう…難易度の顔してるよ〜〜',
      '難易度さん、今上がらなくてもいいと思うんだけど…',
    ];

    const randomMessage = messages[Math.floor(Math.random() * messages.length)];

    const messageMainElement = document.getElementById('difficulty-message-main');
    const messageElement = document.getElementById('difficulty-message');
    if (messageMainElement && messageElement) {
      messageMainElement.textContent = randomMessage;
      messageElement.classList.remove('hidden');
      setTimeout(() => {
        messageElement.classList.add('hidden');
      }, 3500);
    }
  }

  /**
   * ゲーム開始時のコールバックを設定
   */
  public setOnStartGame(callback: (character: Character) => void): void {
    this.onStartGame = callback;
  }

  /**
   * リトライ時のコールバックを設定
   */
  public setOnRetry(callback: () => void): void {
    this.onRetry = callback;
  }

  /**
   * スタートに戻る時のコールバックを設定
   */
  public setOnBackToStart(callback: () => void): void {
    this.onBackToStart = callback;
  }

  /**
   * 現在の画面を取得
   */
  public getCurrentScreen(): Screen {
    return this.currentScreen;
  }

  /**
   * Xで共有
   */
  public shareToX(): void {
    const shareText = `${this.lastSubmittedName} ${this.lastSubmittedScore}点 でランキング入り！

ちょろめのあれっぽいゲーム
(ゆゆ式 Advent Calendar 2025)
https://yuyushikiac2025.web.app?fs #ゆゆ式`;

    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  /**
   * ローディング表示を表示
   */
  public showLoadingIndicator(): void {
    // キャラクター選択画面の上にローディングメッセージを表示
    const characterScreen = document.getElementById('character-select-screen');
    if (characterScreen) {
      // 既存のローディング表示があれば削除
      const existingLoader = document.getElementById('loading-indicator');
      if (existingLoader) {
        existingLoader.remove();
      }

      // ローディング表示を作成
      const loadingDiv = document.createElement('div');
      loadingDiv.id = 'loading-indicator';
      loadingDiv.style.cssText = `
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(255, 255, 255, 0.95);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: clamp(20px, 5vw, 28px);
        font-weight: bold;
        z-index: 1000;
      `;
      loadingDiv.textContent = 'Loading...';

      characterScreen.appendChild(loadingDiv);
    }
  }

  /**
   * ローディング表示を非表示
   */
  public hideLoadingIndicator(): void {
    const loadingIndicator = document.getElementById('loading-indicator');
    if (loadingIndicator) {
      loadingIndicator.remove();
    }
  }
}
